/**
 * TanStack Query hooks para /v1/ninas — FWA-PWA-023.
 *
 * Reusa NinaCreatePayload/UpdatePayload de @sisrep/schemas (single source of truth).
 */
import type { Nina, NinaCreatePayload, NinaListResponse, NinaUpdatePayload } from '@sisrep/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse, apiGet, apiPatch, apiPost, generateIdempotencyKey } from '../api.js';
import { addOutboxItem } from '../outbox/db.js';

// ─── Query keys ───────────────────────────────────────────────────────────

export const ninasKeys = {
  all: ['ninas'] as const,
  list: (filters: NinasListFilters) => ['ninas', 'list', filters] as const,
  detail: (id: string) => ['ninas', 'detail', id] as const,
};

// ─── List query ───────────────────────────────────────────────────────────

export interface NinasListFilters {
  cursor?: string | undefined;
  limit?: number | undefined;
  rama?: string | undefined;
  estatus?: string | undefined;
}

function buildListQueryString(filters: NinasListFilters): string {
  const params = new URLSearchParams();
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.rama) params.set('rama', filters.rama);
  if (filters.estatus) params.set('estatus', filters.estatus);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useNinasList(filters: NinasListFilters = {}) {
  return useQuery<ApiResponse<NinaListResponse>>({
    queryKey: ninasKeys.list(filters),
    queryFn: () => apiGet<NinaListResponse>(`/v1/ninas${buildListQueryString(filters)}`),
  });
}

// ─── Detail query ─────────────────────────────────────────────────────────

export function useNina(id: string | undefined) {
  return useQuery<ApiResponse<Nina>>({
    queryKey: ninasKeys.detail(id ?? ''),
    queryFn: () => apiGet<Nina>(`/v1/ninas/${id}`),
    enabled: Boolean(id),
  });
}

// ─── Create mutation ──────────────────────────────────────────────────────

function isOfflineNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err instanceof TypeError && err.message.toLowerCase().includes('fetch')) return true;
  return false;
}

export function useCreateNina() {
  const qc = useQueryClient();
  return useMutation<ApiResponse<Nina> | { queuedOffline: true }, Error, NinaCreatePayload>({
    mutationKey: ['ninas', 'create'],
    mutationFn: async (payload) => {
      const idempotencyKey = generateIdempotencyKey();
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await addOutboxItem({
            endpoint: '/v1/ninas',
            method: 'POST',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey },
          });
          return { queuedOffline: true };
        }
        return await apiPost<Nina, NinaCreatePayload>('/v1/ninas', payload, { idempotencyKey });
      } catch (err) {
        if (isOfflineNetworkError(err)) {
          await addOutboxItem({
            endpoint: '/v1/ninas',
            method: 'POST',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey },
          });
          return { queuedOffline: true };
        }
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ninasKeys.all });
      void qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

// ─── Update mutation (If-Match + 412 handling) ────────────────────────────

export interface UpdateNinaArgs {
  id: string;
  payload: NinaUpdatePayload;
  etag: string; // ETag actual del recurso (sin quotes — apiPatch agrega)
}

export function useUpdateNina() {
  const qc = useQueryClient();
  return useMutation<ApiResponse<Nina> | { queuedOffline: true }, Error, UpdateNinaArgs>({
    mutationKey: ['ninas', 'update'],
    mutationFn: async ({ id, payload, etag }) => {
      const idempotencyKey = generateIdempotencyKey();
      const endpoint = `/v1/ninas/${id}`;
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await addOutboxItem({
            endpoint,
            method: 'PATCH',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey, 'If-Match': `"${etag}"` },
          });
          return { queuedOffline: true };
        }
        return await apiPatch<Nina, NinaUpdatePayload>(endpoint, payload, {
          idempotencyKey,
          ifMatch: etag,
        });
      } catch (err) {
        if (isOfflineNetworkError(err)) {
          await addOutboxItem({
            endpoint,
            method: 'PATCH',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey, 'If-Match': `"${etag}"` },
          });
          return { queuedOffline: true };
        }
        throw err;
      }
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ninasKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: ninasKeys.all });
      void qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}
